import { Line, LineChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { PromotionBumpChartPoint, PromotionRankMetric } from "../../../../../api/promotion_effectiveness"
import { build_bump_chart_rows, bump_chart_promotions } from "./promotion_effectiveness_utils"

const COLORS = ["#2563eb", "#16a34a", "#dc2626", "#9333ea", "#ea580c", "#0891b2", "#4f46e5", "#be123c"]

interface PromotionBumpChartProps {
  points: PromotionBumpChartPoint[]
  rank_metric: PromotionRankMetric
}

export function PromotionBumpChart({ points, rank_metric }: PromotionBumpChartProps) {
  const rows = build_bump_chart_rows(points)
  const promotions = bump_chart_promotions(points)

  if (rows.length === 0 || promotions.length === 0) {
    return (
      <div class="flex min-h-[260px] flex-col justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-700">
        <div class="font-medium text-gray-900">Peer ranking data needed</div>
        <div class="mt-2 text-gray-600">Upload or derive the fields needed to rank at least two promotions by {rank_metric_label(rank_metric)}:</div>
        <ul class="mt-2 list-disc space-y-1 pl-5">
          {rank_metric_fields(rank_metric).map((field: string) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div class="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 10, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" tick={{ fontSize: 12 }} />
          <YAxis reversed allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip />
          {promotions.map((promotion_name: string, index: number) => (
            <Line
              key={promotion_name}
              type="monotone"
              dataKey={promotion_name}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export function rank_metric_label(rank_metric: PromotionRankMetric): string {
  switch (rank_metric) {
    case "roi":
      return "ROI"
    case "lift_pct":
      return "lift"
    case "incremental_sales":
      return "incremental sales"
    case "actual_spend":
      return "actual spend"
    case "spend_variance":
      return "spend variance"
  }
}

export function rank_metric_fields(rank_metric: PromotionRankMetric): string[] {
  switch (rank_metric) {
    case "roi":
      return ["incremental_margin_dollars", "matched deductions for actual spend"]
    case "lift_pct":
      return ["baseline_sales_dollars", "incremental_sales_dollars or total_sales_dollars"]
    case "incremental_sales":
      return ["incremental_sales_dollars or total_sales_dollars and baseline_sales_dollars"]
    case "actual_spend":
      return ["matched deductions with invoice_amount or split_amount"]
    case "spend_variance":
      return ["planned_amount or promotion_period_allocation", "matched deductions with invoice_amount or split_amount"]
  }
}
